/**
 * The Pick'em standings board (ISSUE-1305): rank, member, points, record and
 * rounds won, for this contest or the whole season. The order and the ranks
 * are the server's (`rankPickemStandings`: points, then rounds won, then
 * correct picks). The API calls a round win a title; the screen never does
 * (GLOSSARY-5 bans the word).
 */
import { usePickemStandingsGet } from "@sportsmet/contest-sdk/private";
import {
  cn,
  InlineBanner,
  SegmentedControl,
  Select,
  Skeleton,
} from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import {
  PICKEM_RESULTS_YOU,
  PICKEM_RETRY,
  PICKEM_STANDINGS_CAPTION,
  PICKEM_STANDINGS_CONTEST_LABEL,
  PICKEM_STANDINGS_EMPTY,
  PICKEM_STANDINGS_FAILED,
  PICKEM_STANDINGS_LOADING,
  PICKEM_STANDINGS_MEMBER_HEADER,
  PICKEM_STANDINGS_POINTS_HEADER,
  PICKEM_STANDINGS_RANK_HEADER,
  PICKEM_STANDINGS_RECORD_HEADER,
  PICKEM_STANDINGS_ROUNDS_WON_HEADER,
  PICKEM_STANDINGS_SCOPE_CONTEST,
  PICKEM_STANDINGS_SCOPE_LABEL,
  PICKEM_STANDINGS_SCOPE_SEASON,
  PICKEM_STANDINGS_SEASON_LABEL,
  pickemRecordLabel,
} from "./pickemCopy";

type typeScope = "contest" | "season";

export const PickemStandings = ({ poolId }: { poolId: string }): ReactNode => {
  const [scope, setScope] = useState<typeScope>("contest");
  const [contestId, setContestId] = useState<string | null>(null);
  const [seasonId, setSeasonId] = useState<string | null>(null);
  const query = usePickemStandingsGet({
    poolId,
    scope,
    contestId: scope === "contest" ? contestId : null,
    seasonId: scope === "season" ? seasonId : null,
  });
  const data = query.data?.data ?? null;

  let body: ReactNode;
  if (query.isLoading) {
    body = (
      <div data-testid="pickem-standings-loading">
        <Skeleton shape="card" label={PICKEM_STANDINGS_LOADING} />
      </div>
    );
  } else if (query.isError || data === null) {
    body = (
      <div data-testid="pickem-standings-failed">
        <InlineBanner
          variant="warning"
          message={PICKEM_STANDINGS_FAILED}
          action={{
            label: PICKEM_RETRY,
            onAction: () => {
              void query.refetch();
            },
          }}
        />
      </div>
    );
  } else {
    const contests = data.options.contests;
    const seasons = data.options.seasons;
    body = (
      <>
        {scope === "contest" && contests.length > 1 ? (
          <Select
            label={PICKEM_STANDINGS_CONTEST_LABEL}
            items={contests.map((contest) => ({
              value: contest.contestId,
              label: contest.name,
            }))}
            value={contestId ?? contests[0]?.contestId}
            onValueChange={setContestId}
          />
        ) : null}
        {scope === "season" && seasons.length > 1 ? (
          <Select
            label={PICKEM_STANDINGS_SEASON_LABEL}
            items={seasons.map((season) => ({
              value: season.seasonId,
              label: season.label,
            }))}
            value={seasonId ?? seasons[0]?.seasonId}
            onValueChange={setSeasonId}
          />
        ) : null}
        {data.rows.length === 0 ? (
          <p
            className="text-body text-ink-secondary"
            data-testid="pickem-standings-empty"
          >
            {PICKEM_STANDINGS_EMPTY}
          </p>
        ) : (
          <table
            className="w-full table-fixed border-collapse"
            data-testid="pickem-standings-table"
          >
            <caption className="pb-2 text-left text-caption text-ink-secondary">
              {`${data.label}. ${PICKEM_STANDINGS_CAPTION}`}
            </caption>
            <thead>
              <tr className="border-b border-hairline text-left text-caption text-ink-secondary">
                <th scope="col" className="w-12 py-2 font-semibold">
                  {PICKEM_STANDINGS_RANK_HEADER}
                </th>
                <th scope="col" className="py-2 font-semibold">
                  {PICKEM_STANDINGS_MEMBER_HEADER}
                </th>
                <th scope="col" className="w-12 py-2 text-right font-semibold">
                  {PICKEM_STANDINGS_POINTS_HEADER}
                </th>
                <th scope="col" className="w-16 py-2 text-right font-semibold">
                  {PICKEM_STANDINGS_RECORD_HEADER}
                </th>
                <th scope="col" className="w-16 py-2 text-right font-semibold">
                  {PICKEM_STANDINGS_ROUNDS_WON_HEADER}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr
                  key={row.userId}
                  className={cn(
                    "border-b border-hairline text-body",
                    row.isViewer ? "font-semibold text-ink" : "text-ink",
                  )}
                  data-testid="pickem-standing-row"
                  data-user-id={row.userId}
                >
                  <td className="py-2 tabular-nums">{row.rank}</td>
                  <td className="truncate py-2">
                    {row.displayName}
                    {row.isViewer ? (
                      <span className="text-ink-secondary">
                        {` (${PICKEM_RESULTS_YOU})`}
                      </span>
                    ) : null}
                  </td>
                  <td className="py-2 text-right tabular-nums">{row.points}</td>
                  <td className="py-2 text-right tabular-nums">
                    {pickemRecordLabel(row)}
                  </td>
                  <td className="py-2 text-right tabular-nums">{row.titles}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="pickem-standings">
      <SegmentedControl
        label={PICKEM_STANDINGS_SCOPE_LABEL}
        options={[
          { value: "contest", label: PICKEM_STANDINGS_SCOPE_CONTEST },
          { value: "season", label: PICKEM_STANDINGS_SCOPE_SEASON },
        ]}
        value={scope}
        onValueChange={(next) => {
          setScope(next === "season" ? "season" : "contest");
        }}
        testId="pickem-standings-scope"
      />
      {body}
    </div>
  );
};
